import React from 'react';
import { Link, useParams } from 'react-router-dom';
import icon from '../assets/icon.png';
import { AppleIcon, PlayIcon } from '../StoreIcons';
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  ShareKind,
  buildOpenInAppUrl,
  useDevice,
} from '../appLinks';

/**
 * Landing page for shared links (/ur/:id, /u/:id, /r/:id).
 *
 * Deliberately shows none of the shared content: the point is to get the
 * person into the app, where it lives. With the app installed the link
 * normally opens it directly; this page is what they see when it did not
 * (app not installed, or iOS set to open the domain in Safari).
 */
const COPY: Record<ShareKind, { title: string; body: string }> = {
  ur: {
    title: 'Someone shared a Rushmore with you',
    body: 'Open it in Rushmore Social to see the full list.',
  },
  u: {
    title: 'Someone shared a profile with you',
    body: 'Open Rushmore Social to see their Rushmores.',
  },
  r: {
    title: 'You’ve been sent a Rushmore to build',
    body: 'Open Rushmore Social to carve your own.',
  },
};

const OpenInAppPage: React.FC<{ kind: ShareKind }> = ({ kind }) => {
  const { id = '' } = useParams<{ id: string }>();
  const { isAndroid, isIOS, isMobile } = useDevice();
  const copy = COPY[kind];

  return (
    <div className="landing open-page">
      <div className="orb orb-magenta" />
      <div className="orb orb-cyan" />

      <main className="open-card">
        <div className="app-icon-ring open-icon-ring">
          <img src={icon} alt="Rushmore Social app icon" className="app-icon" />
        </div>

        <p className="open-brand">Rushmore Social</p>
        <h1 className="open-title">{copy.title}</h1>
        <p className="open-body">
          {isMobile ? copy.body : 'Open this link on your phone to see it in Rushmore Social.'}
        </p>

        {isMobile && id ? (
          <a className="btn primary open-primary" href={buildOpenInAppUrl(kind, id, { isIOS, isAndroid })}>
            Open in Rushmore Social
          </a>
        ) : null}

        <div className="open-divider">
          <span>{isMobile ? 'Don’t have the app?' : 'Get the app'}</span>
        </div>

        <div className="open-stores">
          {!isAndroid && (
            <a className="btn ghost" href={APP_STORE_URL} target="_blank" rel="noreferrer">
              <span className="btn-content">
                <AppleIcon />
                <span>Download on the App Store</span>
              </span>
            </a>
          )}
          {!isIOS && (
            <a className="btn ghost" href={PLAY_STORE_URL} target="_blank" rel="noreferrer">
              <span className="btn-content">
                <PlayIcon />
                <span>Get it on Google Play</span>
              </span>
            </a>
          )}
        </div>
      </main>

      <footer className="open-footer">
        <Link className="footer-link" to="/">ptoarushmore.com</Link>
        <span aria-hidden="true">·</span>
        <Link className="footer-link" to="/terms">Terms</Link>
        <span aria-hidden="true">·</span>
        <Link className="footer-link" to="/privacy">Privacy</Link>
      </footer>
    </div>
  );
};

export default OpenInAppPage;
